import {  Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton } from "@mui/material"
import CloseIcon from '@mui/icons-material/Close'
import { useMemo, useState } from "react"
import { Button, Dropdown, TextField } from 'monday-ui-react-core'
import DatePicker from 'rsuite/DatePicker'

const STRING_ONLY = /^[a-zA-Z_ ]+$/
const ALPHANUMERIC_ONLY = /^[a-zA-Z0-9_ ]+$/

const OrganizationLinksModal = ({open, onClose, onSave}) => {
    const validateStringOnly = (field) => !STRING_ONLY.test(field)
    const validateNumberOnly = (field) => isNaN(+field.replace(/,/g, ''))
    const validateAlphaNumeric = (field) => !ALPHANUMERIC_ONLY.test(field)

    const VERIFICATION_METHOD_OPTIONS_IDS = {
        PAYOUT_STATEMENT: 'payout-statement',
        PRODUCT_BALANCING_STATEMENT: 'product-balancing-statement',
        REGISTRATION_CODE: 'registration-code'
    }

    const VERIFICATION_METHOD_OPTIONS = useMemo(() => [
        {
            value: VERIFICATION_METHOD_OPTIONS_IDS.PAYOUT_STATEMENT,
            label: 'Payout Statement',
        },
        {
            value: VERIFICATION_METHOD_OPTIONS_IDS.PRODUCT_BALANCING_STATEMENT,
            label: 'Gas Balancing Statement',
        },
        {
            value: VERIFICATION_METHOD_OPTIONS_IDS.REGISTRATION_CODE,
            label: 'Registration Code',
        },
    ])

    const REPORTING_MONTH = useMemo(() => [
        { label: '01 - January', value: '01' },
        { label: '02 - February', value: '02' },
        { label: '03 - March', value: '03' },
        { label: '04 - April', value: '04' },
        { label: '05 - May', value: '05' },
        { label: '06 - June', value: '06' },
        { label: '07 - July', value: '07' },
        { label: '08 - August', value: '08' },
        { label: '09 - September', value: '09' },
        { label: '10 - October', value: '10' },
        { label: '11 - November', value: '11' },
        { label: '12 - December', value: '12' },
    ])

    const [sender, setSender] = useState('')
    const [verificationMethod, setVerificationMethod] = useState({
        value: VERIFICATION_METHOD_OPTIONS_IDS.REGISTRATION_CODE,
        label: 'Registration Code',
    },)
    const [payoutId, setPayoutId] = useState('')
    const [reportingMonth, setReportingMonth] = useState('')
    const [reportingCenter, setReportingCenter] = useState('')
    const [statementDate, setStatementDate] = useState('')
    const [ownerNumber, setOwnerNumber] = useState('')
    const [payoutBalance, setPayoutBalance] = useState('')
    const [comulativeImbalance, setComulativeImbalance] = useState('')
    const [registrationCode, setRegistrationCode] = useState('')

    const handleSave = () => {
        const payload = {
            senderNumber: sender,
            verificationMethod: verificationMethod.value === VERIFICATION_METHOD_OPTIONS_IDS.PAYOUT_STATEMENT ? 'PO' : 
                                verificationMethod.value === VERIFICATION_METHOD_OPTIONS_IDS.PRODUCT_BALANCING_STATEMENT ? 'PB' :
                                verificationMethod.value === VERIFICATION_METHOD_OPTIONS_IDS.REGISTRATION_CODE ? 'CD' : '',
            ownerNumber: ownerNumber,
            registrationCode: registrationCode
        }

        onSave(payload)
    }

    const handleSaveButton = () => {
        if (!verificationMethod) return false;

        const isVerificationPayoutStatementValid = 
                verificationMethod.value === VERIFICATION_METHOD_OPTIONS_IDS.PAYOUT_STATEMENT 
            &&  !validateAlphaNumeric(payoutId)
            &&  reportingMonth
            &&  statementDate
            &&  !validateAlphaNumeric(ownerNumber)
            &&  (payoutBalance !== '' && !validateNumberOnly(payoutBalance));
        
        const isVerificationProductBalancingStatementValid = 
                verificationMethod.value === VERIFICATION_METHOD_OPTIONS_IDS.PRODUCT_BALANCING_STATEMENT 
            &&  reportingMonth
            &&  !validateStringOnly(reportingCenter)
            &&  statementDate
            &&  !validateAlphaNumeric(ownerNumber)
            &&  (comulativeImbalance !== '' && !validateNumberOnly(comulativeImbalance));  
            
        const isVerificationRegistrationCodeValid = 
                verificationMethod.value === VERIFICATION_METHOD_OPTIONS_IDS.REGISTRATION_CODE 
            &&  !validateAlphaNumeric(ownerNumber)
            &&  !validateAlphaNumeric(registrationCode);  

        if (!validateAlphaNumeric(sender) &&
            verificationMethod &&
            (isVerificationPayoutStatementValid || isVerificationProductBalancingStatementValid || isVerificationRegistrationCodeValid)
        ) {
            return true;
        }
        return false;
    }

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                fullWidth={false}
                maxWidth='md'
            >
                <DialogTitle>Organization Link</DialogTitle>
                <IconButton
                    aria-label='close'
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500]
                    }}
                >
                    <CloseIcon/>
                </IconButton>

                <DialogContent>
                <Grid container spacing={2} className="">
                            <Grid item xs={12}>
                                <p>
                                    Please select an account verification method
                                    to allow us to verify your identity:
                                </p>
                            </Grid>
                            <Grid item xs={12} sm={8} md={6} lg={5}>
                                <TextField
                                    id="sender"
                                    value={sender}
                                    onChange={(event) =>
                                        setSender(event)
                                    }
                                    title={(<>Sender<b style={{color:'red'}}>*</b></>)}
                                    placeholder="Enter your Sender ID"
                                    size={TextField.sizes.SMALL}
                                    maxLength={128}
                                    validation={{
                                        status:
                                        sender == ''
                                                ? ''
                                                : validateAlphaNumeric(
                                                    sender
                                                    )
                                                ? 'error'
                                                : 'success',
                                        text:
                                        sender == ''
                                                ? ''
                                                : validateAlphaNumeric(
                                                    sender
                                                    )
                                                ? 'Please enter a valid sender.'
                                                : '',
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={8} md={6} lg={7} />
                            <Grid item xs={12} sm={8} md={6} lg={5}>
                                <label className='field-label'>
                                    Select an account verification method<b style={{color:'red'}}>*</b>
                                </label>
                                <Dropdown
                                    size={Dropdown.size.SMALL}
                                    options={VERIFICATION_METHOD_OPTIONS}
                                    onChange={setVerificationMethod}
                                    clearable={false}
                                    value={verificationMethod}
                                    disabled
                                />
                            </Grid>
                            <Grid item xs={12} sm={8} md={6} lg={7} />
                            {verificationMethod?.value ===
                                VERIFICATION_METHOD_OPTIONS[0].value && (
                                <>
                                    <Grid item xs={12} sm={8} md={6} lg={5}>
                                        <TextField
                                            id="payout-id"
                                            value={payoutId}
                                            onChange={(event) =>
                                                setPayoutId(event)
                                            }
                                            title={(<>Payout ID<b style={{color:'red'}}>*</b></>)}
                                            placeholder="Enter your Payout ID"
                                            size={TextField.sizes.SMALL}
                                            maxLength={128}
                                            validation={{
                                                status:
                                                    payoutId == ''
                                                        ? ''
                                                        : validateAlphaNumeric(
                                                              payoutId
                                                          )
                                                        ? 'error'
                                                        : 'success',
                                                text:
                                                    payoutId == ''
                                                        ? ''
                                                        : validateAlphaNumeric(
                                                              payoutId
                                                          )
                                                        ? 'Please enter a valid payout id.'
                                                        : '',
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={8} md={6} lg={7} />
                                    <Grid item xs={12} sm={8} md={6} lg={5}>
                                        <label className='field-label'>
                                            Reporting Month<b style={{color:'red'}}>*</b>
                                        </label>
                                        <br/>
                                        <DatePicker className='date-picker'
                                            placeholder="Reporting Month"
                                            format="yyyy-MM-dd"
                                            onChange={setReportingMonth}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={8} md={6} lg={7} />
                                    <Grid item xs={12} sm={8} md={6} lg={5}>
                                        <label className='field-label'>
                                            Select a Statement Date<b style={{color:'red'}}>*</b>
                                        </label>
                                        <br />
                                        <DatePicker className='date-picker'
                                            placeholder="Statement Date"
                                            format="yyyy-MM-dd"
                                            onChange={setStatementDate}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={8} md={6} lg={7} />
                                    <Grid item xs={12} sm={8} md={6} lg={5}>
                                        <TextField
                                            id="owner-number"
                                            value={ownerNumber}
                                            onChange={(event) =>
                                                setOwnerNumber(event)
                                            }
                                            title={(<>Owner Number<b style={{color:'red'}}>*</b></>)}
                                            placeholder="Enter your Owner Number"
                                            size={TextField.sizes.SMALL}
                                            maxLength={128}
                                            validation={{
                                                status:
                                                    ownerNumber == ''
                                                        ? ''
                                                        : validateAlphaNumeric(
                                                              ownerNumber
                                                          )
                                                        ? 'error'
                                                        : 'success',
                                                text:
                                                    ownerNumber == ''
                                                        ? ''
                                                        : validateAlphaNumeric(
                                                              ownerNumber
                                                          )
                                                        ? 'Please enter a valid owner number'
                                                        : '',
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={8} md={6} lg={7} />
                                    <Grid item xs={12} sm={8} md={6} lg={5}>
                                        <TextField
                                            id="payout-balance"
                                            value={payoutBalance}
                                            onChange={(event) =>
                                                setPayoutBalance(event)
                                            }
                                            title={(<>Payout Balance<b style={{color:'red'}}>*</b></>)}
                                            placeholder="Enter your Payout Balance"
                                            size={TextField.sizes.SMALL}
                                            maxLength={128}
                                            validation={{
                                                status:
                                                    payoutBalance == ''
                                                        ? ''
                                                        : validateNumberOnly(
                                                              payoutBalance
                                                          )
                                                        ? 'error'
                                                        : 'success',
                                                text:
                                                    payoutBalance == ''
                                                        ? ''
                                                        : validateNumberOnly(
                                                              payoutBalance
                                                          )
                                                        ? 'Please enter a valid balance amount'
                                                        : '',
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={6} lg={7} />
                                </>
                            )}
                            {verificationMethod?.value ===
                                VERIFICATION_METHOD_OPTIONS[1].value && (
                                <>
                                    <Grid item xs={12} sm={8} md={6} lg={5}>
                                        <label className='field-label'>
                                            Reporting Month<b style={{color:'red'}}>*</b>
                                        </label>
                                        <Dropdown
                                            size={Dropdown.size.SMALL}
                                            options={REPORTING_MONTH}
                                            onChange={setReportingMonth}
                                            clearable={false}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={8} md={6} lg={7} />
                                    <Grid item xs={12} sm={8} md={6} lg={5}>
                                        <TextField
                                            id="reporting-center"
                                            value={reportingCenter}
                                            onChange={(event) =>
                                                setReportingCenter(event)
                                            }
                                            title={(<>Reporting Center<b style={{color:'red'}}>*</b></>)}
                                            placeholder="Enter your Reporting Center"
                                            size={TextField.sizes.SMALL}
                                            maxLength={128}
                                            validation={{
                                                status:
                                                    reportingCenter == ''
                                                        ? ''
                                                        : validateStringOnly(
                                                              reportingCenter
                                                          )
                                                        ? 'error'
                                                        : 'success',
                                                text:
                                                    reportingCenter == ''
                                                        ? ''
                                                        : validateStringOnly(
                                                              reportingCenter
                                                          )
                                                        ? 'Please enter a valid reporting center'
                                                        : '',
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={8} md={6} lg={7} />
                                    <Grid item xs={12} sm={8} md={6} lg={5}>
                                        <label className='field-label'>
                                            Select a Statement Date<b style={{color:'red'}}>*</b>
                                        </label>
                                        <br />
                                        <DatePicker
                                            placeholder="Statement Date"
                                            format="yyyy - MM - dd"
                                            onChange={setStatementDate}
                                            oneTap
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={8} md={6} lg={7} />
                                    <Grid item xs={12} sm={8} md={6} lg={5}>
                                        <TextField
                                            id="owner-number"
                                            value={ownerNumber}
                                            onChange={(event) =>
                                                setOwnerNumber(event)
                                            }
                                            title={(<>Owner Number<b style={{color:'red'}}>*</b></>)}
                                            placeholder="Enter your Owner Number"
                                            size={TextField.sizes.SMALL}
                                            maxLength={128}
                                            validation={{
                                                status:
                                                    ownerNumber == ''
                                                        ? ''
                                                        : validateAlphaNumeric(
                                                              ownerNumber
                                                          )
                                                        ? 'error'
                                                        : 'success',
                                                text:
                                                    ownerNumber == ''
                                                        ? ''
                                                        : validateAlphaNumeric(
                                                              ownerNumber
                                                          )
                                                        ? 'Please enter a valid owner number'
                                                        : '',
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={8} md={6} lg={7} />
                                    <Grid item xs={12} sm={8} md={6} lg={5}>
                                        <TextField
                                            id="cumulative-imbalance"
                                            value={comulativeImbalance}
                                            onChange={(event) =>
                                                setComulativeImbalance(event)
                                            }
                                            title={(<>Cumulative Imbalance<b style={{color:'red'}}>*</b></>)}
                                            placeholder="Enter your Cumulative Imbalance"
                                            size={TextField.sizes.SMALL}
                                            maxLength={128}
                                            validation={{
                                                status:
                                                    comulativeImbalance == ''
                                                        ? ''
                                                        : validateNumberOnly(
                                                              comulativeImbalance
                                                          )
                                                        ? 'error'
                                                        : 'success',
                                                text:
                                                    comulativeImbalance == ''
                                                        ? ''
                                                        : validateNumberOnly(
                                                              comulativeImbalance
                                                          )
                                                        ? 'Please enter a valid balance amount'
                                                        : '',
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={6} lg={7} />
                                </>
                            )}
                            {verificationMethod?.value ===
                                VERIFICATION_METHOD_OPTIONS[2].value && (
                                <>
                                    <Grid item xs={12} sm={8} md={6} lg={5}>
                                        <TextField
                                            id="owner-number"
                                            value={ownerNumber}
                                            onChange={(event) =>
                                                setOwnerNumber(event)
                                            }
                                            title={(<>Owner Number<b style={{color:'red'}}>*</b></>)}
                                            placeholder="Enter your Owner Number"
                                            size={TextField.sizes.SMALL}
                                            maxLength={128}
                                            validation={{
                                                status:
                                                    ownerNumber == ''
                                                        ? ''
                                                        : validateAlphaNumeric(
                                                              ownerNumber
                                                          )
                                                        ? 'error'
                                                        : 'success',
                                                text:
                                                    ownerNumber == ''
                                                        ? ''
                                                        : validateAlphaNumeric(
                                                              ownerNumber
                                                          )
                                                        ? 'Please enter a valid owner number'
                                                        : '',
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={8} md={6} lg={7} />
                                    <Grid item xs={12} sm={8} md={6} lg={5}>
                                        <TextField
                                            id="registration-code"
                                            value={registrationCode}
                                            onChange={(event) =>
                                                setRegistrationCode(event)
                                            }
                                            title={(<>Registration Code<b style={{color:'red'}}>*</b></>)}
                                            placeholder="Enter your Registration Code"
                                            size={TextField.sizes.SMALL}
                                            maxLength={128}
                                            validation={{
                                                status:
                                                    registrationCode == ''
                                                        ? ''
                                                        : validateAlphaNumeric(
                                                              registrationCode
                                                          )
                                                        ? 'error'
                                                        : 'success',
                                                text:
                                                    registrationCode == ''
                                                        ? ''
                                                        : validateAlphaNumeric(
                                                              registrationCode
                                                          )
                                                        ? 'Please enter a valid registration code'
                                                        : '',
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={6} lg={7} />
                                </>
                            )}
                        </Grid>
                </DialogContent>

                <DialogActions>
                    <Button className="cancel" onClick={onClose} marginRight={2}>Cancel</Button>
                    <Button className="success" disabled={!handleSaveButton()} onClick={handleSave} marginRight={2}>Save</Button>
                </DialogActions>

            </Dialog>
        </>
    )
}

export default OrganizationLinksModal